import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, CircleHelp, RotateCcw, ThumbsUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CaptureStatus, ConfirmCaptureResponse, ReasonCode, VerdictRequest } from '@/lib/api/types';
import { REASON_WORDS, humanise } from './review-model';
import { Sheet } from './Sheet';

/** What the last action on this screen came to, in the operator's words. */
export type FooterOutcome =
  | { kind: 'confirmed'; response: ConfirmCaptureResponse }
  | { kind: 'escalated' }
  | { kind: 'verdict'; verdict: VerdictRequest['verdict']; status: CaptureStatus };

const TEXTAREA_CLASS =
  'w-full rounded-control border border-border-strong bg-transparent px-3 py-2 text-sm text-surface-foreground placeholder:text-muted focus-visible:border-brand focus-visible:outline-none';

interface OperatorFooterProps {
  busy: boolean;
  canConfirm: boolean;
  canEscalate: boolean;
  onConfirm: () => void;
  onEscalate: (question: string) => Promise<boolean>;
}

/**
 * The operator's pinned actions (ADR-006 decision 3, wireframe R-A). "Done,
 * next item" sends every edit, add and reason on the screen in one confirm.
 * Ask opens a sheet for the question, because a manager handed a capture
 * with no question has nothing to answer, and text entry belongs in a sheet
 * where the keyboard covers nothing the operator still needs.
 */
export function OperatorFooter({ busy, canConfirm, canEscalate, onConfirm, onEscalate }: OperatorFooterProps) {
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');

  async function send() {
    const sent = await onEscalate(question.trim());
    if (sent) {
      setAsking(false);
      setQuestion('');
    }
  }

  return (
    <>
      <div className="flex gap-2">
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="px-4"
          disabled={busy || !canEscalate}
          onClick={() => setAsking(true)}
        >
          <CircleHelp aria-hidden="true" />
          Ask
        </Button>
        <Button type="button" size="lg" className="flex-1 px-4" disabled={busy || !canConfirm} onClick={onConfirm}>
          <Check aria-hidden="true" />
          {busy ? 'Saving...' : 'Done, next item'}
        </Button>
      </div>
      <Sheet open={asking} title="Ask manager" onClose={() => setAsking(false)}>
        <div className="flex flex-col gap-3">
          <label htmlFor="escalate-question" className="text-xs text-muted">
            Question
          </label>
          <textarea
            id="escalate-question"
            rows={3}
            value={question}
            disabled={busy}
            onChange={(event) => setQuestion(event.target.value)}
            className={TEXTAREA_CLASS}
          />
          <Button type="button" size="lg" disabled={busy || question.trim() === ''} onClick={() => void send()}>
            Send
          </Button>
        </div>
      </Sheet>
    </>
  );
}

interface ReviewerFooterProps {
  busy: boolean;
  pendingChanges: number;
  canDecide: boolean;
  onVerdict: (verdict: 'approve' | 'reject' | 'request_recapture', reason?: string) => Promise<boolean>;
}

const REASON_SHEET: Record<'reject' | 'request_recapture', { title: string; submit: string }> = {
  reject: { title: 'Reject', submit: 'Reject capture' },
  request_recapture: { title: 'Retake request', submit: 'Send request' },
};

/**
 * The reviewer's pinned actions (ADR-006 decision 3): approve, request a
 * retake and reject through the section 12 verdict route. A reject or a
 * retake request asks for the reason in a sheet first, because the API
 * refuses either without one and the operator reads it.
 */
export function ReviewerFooter({ busy, pendingChanges, canDecide, onVerdict }: ReviewerFooterProps) {
  const [pending, setPending] = useState<'reject' | 'request_recapture' | null>(null);
  const [reason, setReason] = useState('');

  async function decide() {
    if (!pending) return;
    const done = await onVerdict(pending, reason.trim());
    if (done) {
      setPending(null);
      setReason('');
    }
  }

  return (
    <>
      <div className="flex gap-2">
        <Button
          type="button"
          size="lg"
          className="flex-1 px-3"
          disabled={busy || !canDecide}
          onClick={() => void onVerdict('approve')}
        >
          <ThumbsUp aria-hidden="true" />
          {busy ? 'Saving...' : pendingChanges > 0 ? 'Save, approve' : 'Approve'}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="px-3"
          disabled={busy || !canDecide}
          onClick={() => setPending('request_recapture')}
        >
          <RotateCcw aria-hidden="true" />
          Retake
        </Button>
        <Button
          type="button"
          size="lg"
          variant="destructive"
          className="px-3"
          disabled={busy || !canDecide}
          onClick={() => setPending('reject')}
        >
          <X aria-hidden="true" />
          Reject
        </Button>
      </div>
      <Sheet open={pending !== null} title={pending ? REASON_SHEET[pending].title : ''} onClose={() => setPending(null)}>
        <div className="flex flex-col gap-3">
          <label htmlFor="verdict-reason" className="text-xs text-muted">
            Reason
          </label>
          <textarea
            id="verdict-reason"
            rows={3}
            value={reason}
            disabled={busy}
            onChange={(event) => setReason(event.target.value)}
            className={TEXTAREA_CLASS}
          />
          <Button
            type="button"
            size="lg"
            variant={pending === 'reject' ? 'destructive' : 'primary'}
            disabled={busy || reason.trim() === ''}
            onClick={() => void decide()}
          >
            {pending ? REASON_SHEET[pending].submit : ''}
          </Button>
        </div>
      </Sheet>
    </>
  );
}

function reasonWords(code: ReasonCode | undefined): string {
  return code ? REASON_WORDS[code] : 'it needs a second look';
}

const VERDICT_WORDS: Record<VerdictRequest['verdict'], string> = {
  approve: 'Approved',
  reject: 'Rejected',
  request_recapture: 'Retake requested',
  edit: 'Saved',
};

/**
 * What the last action came to, in one or two short lines above the
 * pinned actions. Next item opens the viewfinder for the same job when the
 * caller knows it, and the Capture tab otherwise. After a confirm this is the answer the operator waited
 * for: approved on the spot, or with the manager and why, with every field
 * still outstanding named.
 */
export function OutcomePanel({ outcome, nextTo = '/capture' }: { outcome: FooterOutcome; nextTo?: string }) {
  if (outcome.kind === 'escalated') {
    return (
      <OutcomeBox tone="border-border-strong" nextTo={nextTo}>
        <p>Sent to manager</p>
      </OutcomeBox>
    );
  }

  if (outcome.kind === 'verdict') {
    return (
      <OutcomeBox tone="border-border-strong">
        <p>{VERDICT_WORDS[outcome.verdict]}</p>
      </OutcomeBox>
    );
  }

  const { response } = outcome;
  const approved = response.status === 'APPROVED' || response.status === 'AUTO_APPROVED';
  const outstanding = response.outstanding ?? [];

  return (
    <OutcomeBox tone={approved || response.status === undefined ? 'border-success' : 'border-warning'} nextTo={nextTo}>
      {approved ? (
        <p className="text-success">Approved</p>
      ) : response.status === undefined ? (
        <p className="text-success">Confirmed</p>
      ) : (
        <p className="text-warning">Sent to your manager, because {reasonWords(response.reasonCode)}.</p>
      )}
      {outstanding.length > 0 && <p>Still needed: {outstanding.map((name) => humanise(name)).join(', ')}.</p>}
    </OutcomeBox>
  );
}

/** `nextTo` is where Next item leads; omitted, the box offers no link. */
function OutcomeBox({ tone, nextTo, children }: { tone: string; nextTo?: string; children: ReactNode }) {
  return (
    <div className={`flex items-center gap-3 rounded-card border px-3 py-2 text-sm ${tone}`} role="status">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>
      {nextTo && (
        <Link
          to={nextTo}
          className="inline-flex min-h-11 shrink-0 items-center rounded-control bg-brand px-4 text-sm font-medium text-brand-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Next item
        </Link>
      )}
    </div>
  );
}
